import React, { useState } from 'react';
import {
  CreditCard,
  Smartphone,
  Building,
  Wallet,
  DollarSign,
  GripVertical,
  Trash2,
  Plus,
} from 'lucide-react';

type TabType = 'payment-types' | 'registration-form';

interface PaymentMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  enabled: boolean;
}

interface FormField {
  id: string;
  type: string;
  label: string;
  required: boolean;
}

const defaultPaymentMethods: PaymentMethod[] = [
  { id: 'stripe', name: 'Stripe', description: 'Global online payment gateway for cards, wallets, and recurring payments', icon: <CreditCard className="w-5 h-5" />, enabled: true },
  { id: 'apple_pay', name: 'Apple Pay', description: 'Mobile wallet allowing parents to pay instantly using their Apple devices', icon: <Smartphone className="w-5 h-5" />, enabled: true },
  { id: 'google_pay', name: 'Google Pay', description: 'Secure mobile wallet for Android and web-based payments', icon: <Smartphone className="w-5 h-5" />, enabled: true },
  { id: 'paypal', name: 'PayPal', description: 'Global payment wallet widely used for online payments and donations', icon: <Wallet className="w-5 h-5" />, enabled: false },
  { id: 'bank_transfer', name: 'Bank Transfer', description: 'Direct transfer between bank accounts, local or international', icon: <Building className="w-5 h-5" />, enabled: false },
  { id: 'cash', name: 'Cash Payment at School', description: 'Allows parents to pay physically at the school office', icon: <DollarSign className="w-5 h-5" />, enabled: false },
  { id: 'credit_card', name: 'Credit / Debit Card', description: 'Standard card payments via Visa, Mastercard, Amex, etc.', icon: <CreditCard className="w-5 h-5" />, enabled: true },
];

const defaultFormFields: FormField[] = [
  { id: '1', type: 'text', label: 'Student Details', required: true },
  { id: '2', type: 'image', label: 'Student Image', required: false },
  { id: '3', type: 'select', label: 'Selection Inquirer', required: false },
  { id: '4', type: 'text', label: 'Information Inquiry', required: false },
];

export function FinanceSettingsSection() {
  const [activeTab, setActiveTab] = useState<TabType>('payment-types');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(defaultPaymentMethods);
  const [useDefaultForm, setUseDefaultForm] = useState(true);
  const [formFields, setFormFields] = useState<FormField[]>(defaultFormFields);

  const handleTogglePayment = (id: string) => {
    setPaymentMethods((prev) =>
      prev.map((pm) => (pm.id === id ? { ...pm, enabled: !pm.enabled } : pm))
    );
  };

  const handleRemoveField = (id: string) => {
    setFormFields((prev) => prev.filter((f) => f.id !== id));
  };

  const tabs = [
    { id: 'payment-types' as const, label: 'Payment Types' },
    { id: 'registration-form' as const, label: 'Registration Form' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Tabs */}
      <div className="border-b border-gray-200 px-6">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {activeTab === 'payment-types' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Payment Types</h2>
            <p className="text-sm text-gray-500 mb-6">
              Enable or disable payment methods available to students and parents.
            </p>

            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    method.enabled ? 'border-teal-200 bg-teal-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      method.enabled ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {method.icon}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{method.name}</p>
                      <p className="text-sm text-gray-500">{method.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm ${method.enabled ? 'text-teal-600' : 'text-gray-400'}`}>
                      {method.enabled ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      onClick={() => handleTogglePayment(method.id)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        method.enabled ? 'bg-teal-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          method.enabled ? 'translate-x-6' : ''
                        }`}
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'registration-form' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Registration Form</h2>
                <p className="text-sm text-gray-500">
                  Customize the registration form for new students.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Use our Default Set</span>
                <button
                  onClick={() => setUseDefaultForm(!useDefaultForm)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    useDefaultForm ? 'bg-teal-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      useDefaultForm ? 'translate-x-6' : ''
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Form Builder */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Form Fields</h3>
                <div className="space-y-2">
                  {formFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{field.label}</p>
                        <p className="text-xs text-gray-500">{field.type}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveField(field.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <button className="flex items-center gap-2 mt-4 text-sm text-teal-600 hover:text-teal-700">
                  <Plus className="w-4 h-4" />
                  Add Field
                </button>

                {/* Field Types */}
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Available Field Types</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['Text Input', 'Dropdown', 'Checkbox', 'Date Picker', 'File Upload', 'Email'].map((type) => (
                      <button
                        key={type}
                        className="px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Form Preview */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Preview</h3>
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="space-y-4">
                    {formFields.map((field) => (
                      <div key={field.id}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {field.type === 'image' ? (
                          <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-white">
                            <span className="text-xs text-gray-400">Upload</span>
                          </div>
                        ) : field.type === 'select' ? (
                          <select className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm">
                            <option>Select option</option>
                          </select>
                        ) : (
                          <input
                            type="text"
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                            disabled
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
